// src/services/audit-service.ts

import { DataService } from './data-service';
import { 
  AuditLog, 
  AuditLogFilters, 
  AuditLogResponse, 
  ParsedAuditLog,
  AuditTimelineItem,
  AuditAction,
  AuditMetadata 
} from '../types/Audit.types';

// Interface for the API response structure you're getting
interface ApiResponse<T = any> {
  success: boolean;
  message: string | null;
  data: {
    items: { [key: string]: T };
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  token: string | null;
}

// Interface for the AuditLogResponse that your components expect
interface AuditLogResponseWithItems {
  items: ParsedAuditLog[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

// Cache for audit data to prevent infinite loops
class AuditCache {
  private cache = new Map<string, { data: any; timestamp: number; }>();
  private readonly TTL = 30000; // 30 seconds cache

  set(key: string, data: any): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  get(key: string): any | null {
    const item = this.cache.get(key);
    if (!item) return null;
    
    if (Date.now() - item.timestamp > this.TTL) {
      this.cache.delete(key);
      return null;
    }
    
    return item.data;
  }

  clear(): void {
    this.cache.clear();
  }

  delete(key: string): void {
    this.cache.delete(key);
  }
}

export class AuditService {
  private static cache = new AuditCache();
  private static pendingRequests = new Map<string, Promise<any>>();

  /**
   * Parse audit log and handle JSON fields safely
   */
  static parseAuditLog(log: AuditLog): ParsedAuditLog {
    return {
      ...log,
      oldValues: log.oldValues ? this.safeJsonParse(log.oldValues) : undefined,
      newValues: log.newValues ? this.safeJsonParse(log.newValues) : undefined,
      metadata: log.metadata ? this.safeJsonParse(log.metadata) : undefined,
      parsedTimestamp: new Date(log.timestamp)
    };
  }

  /**
   * Safely parse JSON strings
   */
  private static safeJsonParse(jsonString: string): any {
    try {
      return JSON.parse(jsonString);
    } catch {
      return null;
    }
  }

  /**
   * Convert API response items object to array
   */
  private static convertItemsToArray<T>(apiResponse: ApiResponse<T>): T[] {
    if (!apiResponse.success) {
      throw new Error(apiResponse.message || 'API request failed');
    }
    
    return apiResponse.data?.items ? Object.values(apiResponse.data.items) : [];
  }

  /**
   * Create cache key for requests
   */
  private static createCacheKey(endpoint: string, params: Record<string, any> = {}): string {
    const sortedParams = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${endpoint}?${sortedParams}`;
  }

  /**
   * Generic request handler with caching and deduplication
   */
  private static async makeRequest<T>(
    endpoint: string, 
    params: Record<string, any> = {},
    useCache: boolean = true
  ): Promise<T> {
    const cacheKey = this.createCacheKey(endpoint, params);
    
    // Check cache first
    if (useCache) {
      const cached = this.cache.get(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Check if request is already pending
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)!;
    }

    // Build query string
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const url = queryParams.toString() ? `${endpoint}?${queryParams}` : endpoint;

    // Make request
    const requestPromise = (async () => {
      try {
        const response = await DataService.get(url);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch data from ${endpoint}`);
        }

        const data = await response.json();
        
        // Cache the result
        if (useCache) {
          this.cache.set(cacheKey, data);
        }
        
        return data;
      } finally {
        // Remove from pending requests
        this.pendingRequests.delete(cacheKey);
      }
    })();

    // Store pending request
    this.pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  }

  /**
   * Get audit logs with filters and pagination - Returns the format your components expect
   */
  static async getAuditLogs(
    filters: AuditLogFilters = {},
    page: number = 1,
    pageSize: number = 50,
    useCache: boolean = true
  ): Promise<AuditLogResponseWithItems> {
    try {
      const params = {
        pageNumber: page,
        pageSize,
        ...filters
      };

      const apiData: ApiResponse<AuditLog> = await this.makeRequest(
        'api/audit/logs',
        params,
        useCache
      );

      const logs = this.convertItemsToArray(apiData);
      const parsedLogs = logs.map(log => this.parseAuditLog(log));

      // Return the format your components expect
      return {
        items: parsedLogs,
        totalCount: apiData.data.totalCount,
        pageNumber: apiData.data.pageNumber,
        pageSize: apiData.data.pageSize,
        totalPages: apiData.data.totalPages,
        hasNext: apiData.data.hasNext,
        hasPrevious: apiData.data.hasPrevious
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  }

  /**
   * Get audit logs as simple array - For components that just need the logs
   */
  static async getAuditLogsArray(
    filters: AuditLogFilters = {},
    page: number = 1,
    pageSize: number = 50,
    useCache: boolean = true
  ): Promise<ParsedAuditLog[]> {
    const response = await this.getAuditLogs(filters, page, pageSize, useCache);
    return response.items;
  }

  /**
   * Get audit logs for a specific entity (with improved caching)
   */
  static async getEntityAuditLogs(
    entityType: string, 
    entityId: string,
    page: number = 1,
    pageSize: number = 50,
    useCache: boolean = true
  ): Promise<ParsedAuditLog[]> {
    try {
      const params = {
        pageNumber: page,
        pageSize
      };

      const apiData: ApiResponse<AuditLog> = await this.makeRequest(
        `api/audit/entity/${entityType}/${entityId}`,
        params,
        useCache
      );

      const logs = this.convertItemsToArray(apiData);
      return logs.map(log => this.parseAuditLog(log));
    } catch (error) {
      console.error('Error fetching entity audit logs:', error);
      throw error;
    }
  }

  /**
   * Get user activity logs
   */
  static async getUserActivity(
    userId: string,
    dateFrom?: string,
    dateTo?: string,
    page: number = 1,
    pageSize: number = 50,
    useCache: boolean = true
  ): Promise<ParsedAuditLog[]> {
    try {
      const params: any = {
        pageNumber: page,
        pageSize
      };

      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const apiData: ApiResponse<AuditLog> = await this.makeRequest(
        `api/audit/user/${userId}`,
        params,
        useCache
      );

      const logs = this.convertItemsToArray(apiData);
      return logs.map(log => this.parseAuditLog(log));
    } catch (error) {
      console.error('Error fetching user activity:', error);
      throw error;
    }
  }

  /**
   * Get shipment timeline (including related boxes, products, etc.)
   */
  static async getShipmentTimeline(
    shipmentId: string,
    useCache: boolean = true
  ): Promise<ParsedAuditLog[]> {
    try {
      const apiData: ApiResponse<AuditLog> = await this.makeRequest(
        `api/audit/shipment/${shipmentId}/timeline`,
        {},
        useCache
      );

      const logs = this.convertItemsToArray(apiData);
      return logs.map(log => this.parseAuditLog(log));
    } catch (error) {
      console.error('Error fetching shipment timeline:', error);
      throw error;
    }
  }

  /**
   * Get recent activity across the system
   */
  static async getRecentActivity(
    count: number = 50,
    entityTypes?: string[],
    useCache: boolean = true
  ): Promise<ParsedAuditLog[]> {
    try {
      const params: any = {
        pageSize: count,
        pageNumber: 1
      };

      if (entityTypes && entityTypes.length > 0) {
        params.entityTypes = entityTypes.join(',');
      }

      const apiData: ApiResponse<AuditLog> = await this.makeRequest(
        'api/audit/recent',
        params,
        useCache
      );

      const logs = this.convertItemsToArray(apiData);
      return logs.map(log => this.parseAuditLog(log));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  }

  /**
   * Get timeline data with filters
   */
  static async getTimelineData(
    filters: AuditLogFilters = {},
    limit: number = 50,
    useCache: boolean = true
  ): Promise<AuditTimelineItem[]> {
    try {
      const params = {
        pageSize: limit,
        pageNumber: 1,
        ...filters
      };

      const apiData: ApiResponse<AuditLog> = await this.makeRequest(
        'api/audit/timeline',
        params,
        useCache
      );

      const logs = this.convertItemsToArray(apiData);
      const parsedLogs = logs.map(log => this.parseAuditLog(log));
      return this.convertToTimelineItems(parsedLogs);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
      throw error;
    }
  }

  /**
   * Export audit logs to CSV or Excel
   */
  static async exportAuditLogs(
    filters: AuditLogFilters = {},
    format: 'csv' | 'excel' = 'excel'
  ): Promise<Blob> {
    try {
      const params = {
        format,
        ...filters
      };

      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await DataService.get(`api/audit/export?${queryParams}`);
      
      if (!response.ok) {
        throw new Error('Failed to export audit logs');
      }

      return await response.blob();
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  }

  /**
   * Get audit statistics
   */
  static async getAuditStatistics(
    dateFrom?: string,
    dateTo?: string,
    useCache: boolean = true
  ): Promise<{
    totalLogs: number;
    todayLogs: number;
    activeUsers: number;
    topEntityTypes: Array<{ type: string; count: number }>;
  }> {
    try {
      const params: any = {};
      
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      return await this.makeRequest('api/audit/statistics', params, useCache);
    } catch (error) {
      console.error('Error fetching audit statistics:', error);
      throw error;
    }
  }

  /**
   * Convert audit logs to timeline format
   */
  static convertToTimelineItems(logs: ParsedAuditLog[]): AuditTimelineItem[] {
    return logs.map(log => ({
      id: log.id,
      timestamp: log.parsedTimestamp,
      title: this.getActionTitleByNumber(log.action, log.entityType),
      description: log.description,
      action: log.action,
      user: {
        id: log.userId,
        name: log.userName
      },
      metadata: log.metadata,
      changes: this.extractChanges(log),
      relatedEntities: this.extractRelatedEntities(log)
    }));
  }

  /**
   * Get action title by number (handles numeric actions from your API)
   */
  private static getActionTitleByNumber(action: number, entityType: string): string {
    const actionMap: Record<number, string> = {
      1: 'Created',
      2: 'Updated',
      3: 'Deleted',
      4: 'Archived',
      5: 'Received',
      6: 'Shipped',
      7: 'Moved',
      8: 'Allocated',
      9: 'Deallocated',
      10: 'Bulk Operation',
      11: 'Status Changed'
    };
    
    const actionName = actionMap[action] || 'Modified';
    return `${entityType} ${actionName}`;
  }

  /**
   * Extract field changes from audit log
   */
  private static extractChanges(log: ParsedAuditLog) {
    if (!log.oldValues || !log.newValues) return [];

    const changes: { field: string; oldValue: any; newValue: any }[] = [];
    
    // Compare old and new values
    const allFields = new Set([
      ...Object.keys(log.oldValues || {}),
      ...Object.keys(log.newValues || {})
    ]);

    allFields.forEach(field => {
      const oldValue = log.oldValues?.[field];
      const newValue = log.newValues?.[field];
      
      if (oldValue !== newValue) {
        changes.push({
          field,
          oldValue,
          newValue
        });
      }
    });

    return changes;
  }

  /**
   * Extract related entities from metadata
   */
  private static extractRelatedEntities(log: ParsedAuditLog) {
    if (!log.metadata?.RelationData) return [];

    return Object.entries(log.metadata.RelationData).map(([key, value]) => ({
      type: key.replace('Id', ''),
      id: value,
      name: log.metadata?.InfoData?.[key.replace('Id', 'Name')] || undefined
    }));
  }

  /**
   * Get action icon
   */
  static getActionIcon(action: AuditAction | number): string {
    // Handle both enum and number cases
    const actionValue = typeof action === 'number' ? action : Number(action);
    
    const iconMap: Record<number, string> = {
      1: 'AddCircle',      // Create
      2: 'Edit',           // Update
      3: 'DeleteForever',  // Delete
      4: 'Archive',        // Archive
      5: 'GetApp',         // Receive
      6: 'LocalShipping',  // Ship
      7: 'SwapHoriz',      // Move
      8: 'Assignment',     // Allocate
      9: 'AssignmentReturn', // Deallocate
      10: 'BatchPrediction', // Bulk Update
      11: 'ChangeCircle'   // Status Change
    };
    
    return iconMap[actionValue] || 'Info';
  }

  /**
   * Get action color
   */
  static getActionColor(action: AuditAction | number): 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' {
    // Handle both enum and number cases
    const actionValue = typeof action === 'number' ? action : Number(action);
    
    const colorMap: Record<number, any> = {
      1: 'success',    // Create
      2: 'info',       // Update
      3: 'error',      // Delete
      4: 'warning',    // Archive
      5: 'success',    // Receive
      6: 'primary',    // Ship
      7: 'warning',    // Move
      8: 'info',       // Allocate
      9: 'warning',    // Deallocate
      10: 'secondary', // Bulk Update
      11: 'primary'    // Status Change
    };
    
    return colorMap[actionValue] || 'primary';
  }

  /**
   * Format timestamp for display
   */
  static formatTimestamp(timestamp: Date, format: 'full' | 'date' | 'time' | 'relative' = 'full'): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    
    switch (format) {
      case 'relative':
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
        return timestamp.toLocaleDateString();
      
      case 'date':
        return timestamp.toLocaleDateString();
      
      case 'time':
        return timestamp.toLocaleTimeString();
      
      case 'full':
      default:
        return timestamp.toLocaleString();
    }
  }

  /**
   * Group audit logs by date
   */
  static groupLogsByDate(logs: ParsedAuditLog[]): { [date: string]: ParsedAuditLog[] } {
    return logs.reduce((groups, log) => {
      const date = log.parsedTimestamp.toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(log);
      return groups;
    }, {} as { [date: string]: ParsedAuditLog[] });
  }

  /**
   * Filter logs by entity type
   */
  static filterByEntityType(logs: ParsedAuditLog[], entityTypes: string[]): ParsedAuditLog[] {
    if (!entityTypes.length) return logs;
    return logs.filter(log => entityTypes.includes(log.entityType));
  }

  /**
   * Filter logs by action
   */
  static filterByAction(logs: ParsedAuditLog[], actions: number[]): ParsedAuditLog[] {
    if (!actions.length) return logs;
    return logs.filter(log => actions.includes(log.action));
  }

  /**
   * Filter logs by date range
   */
  static filterByDateRange(logs: ParsedAuditLog[], startDate: Date, endDate: Date): ParsedAuditLog[] {
    return logs.filter(log => 
      log.parsedTimestamp >= startDate && log.parsedTimestamp <= endDate
    );
  }

  /**
   * Search logs by text
   */
  static searchLogs(logs: ParsedAuditLog[], searchText: string): ParsedAuditLog[] {
    if (!searchText.trim()) return logs;
    
    const search = searchText.toLowerCase();
    return logs.filter(log => 
      log.description.toLowerCase().includes(search) ||
      log.entityType.toLowerCase().includes(search) ||
      log.userName.toLowerCase().includes(search) ||
      log.entityId.toLowerCase().includes(search)
    );
  }

  /**
   * Invalidate cache for specific entity
   */
  static invalidateEntityCache(entityType: string, entityId: string): void {
    // Clear cache entries related to this entity
    const keysToDelete: string[] = [];
    
    // This is a simplified approach - in production you might want more sophisticated cache invalidation
    this.cache.clear();
  }

  /**
   * Clear all cache
   */
  static clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Force refresh data (bypass cache)
   */
  static async forceRefresh<T>(
    method: (...args: any[]) => Promise<T>,
    ...args: any[]
  ): Promise<T> {
    // Clear relevant cache
    this.cache.clear();
    
    // Call method with cache disabled
    return method.apply(this, args);
  }
}